/**
 * The product's terms, privacy notice and FAQ.
 *
 * Written from what the code actually does, not from a template: the data
 * named here is the data the tables hold, the third parties named are the ones
 * the app really calls, and the AI paragraph says plainly that business
 * figures are sent to a language model, because they are.
 *
 * A privacy notice that describes a different product than the one running is
 * worse than none — it is a written promise nobody kept.
 *
 * Reviewed by a lawyer on 2026-08-26. Still not legal advice from this file:
 * when the system changes, this has to change with it, and the paragraph that
 * stops being true is the one nobody remembers to update.
 */

/**
 * Shown to the reader as "last updated", so it moves when the text does —
 * a notice dated before its own contents is the kind of small lie that makes
 * somebody doubt the rest of it.
 */
export const LEGAL_VERSION = '2026-08-26';

/**
 * Who is answerable for the data. Appears verbatim in both documents.
 *
 * Two things still worth confirming against the acta constitutiva, since a
 * privacy notice naming the wrong entity identifies nobody:
 *
 * - The denominación social has to match the deed character for character.
 *   Registered names often carry a word the trading name drops — "Quantifyr
 *   Technologies", say — and that is the version that belongs here.
 * - The domicilio should be the company's, and it is published and indexed.
 *   The law wants an address for the responsable; it does not have to be a
 *   home.
 */
export const OPERATOR = {
  /** Registered name of the person or company running Quantifyr. */
  legalName: 'Quantifyr, S.A. de C.V.',
  /** Address for privacy requests. */
  address: 'Blvd. Bosque Real 43, Huixquilucan, Estado de México, C.P. 52774',
  /**
   * Where ARCO requests and support arrive.
   *
   * A real mailbox as of 2026-08-21: Cloudflare Email Routing forwards it to a
   * monitored inbox. `hola@` still routes to the same place and is left alive
   * for anyone who read it here before this changed — a published address that
   * bounces is worse than an extra alias.
   */
  email: 'support@quantifyrapp.com',
};

export interface Section {
  heading: { es: string; en: string };
  body: Array<{ es: string; en: string }>;
}

// ── Terms ───────────────────────────────────────────────────────────────────

export const TERMS: Section[] = [
  {
    heading: { es: 'Qué es Quantifyr', en: 'What Quantifyr is' },
    body: [
      {
        es: 'Quantifyr es una herramienta para que un negocio pequeño registre sus ventas, clientes, costos e inventario, y reciba lecturas en lenguaje llano sobre cómo va. Es una herramienta de información, no un sistema contable ni un punto de venta, y no sustituye a tu contador.',
        en: 'Quantifyr is a tool for a small business to record its sales, customers, costs and stock, and get plain-language readings of how it is doing. It is an information tool, not an accounting system or a point of sale, and it does not replace your accountant.',
      },
      {
        es: 'Los números que te muestra salen de lo que tú registras o de lo que sincronizan tus servicios de cobro. Si esos datos están mal, las lecturas estarán mal.',
        en: 'The figures it shows come from what you record or from what your payment services sync. If that data is wrong, the readings will be wrong.',
      },
    ],
  },
  {
    heading: { es: 'Tu cuenta', en: 'Your account' },
    body: [
      {
        es: 'Necesitas una cuenta y un correo verificado. Eres responsable de lo que ocurra bajo tu cuenta y de mantener tu contraseña a salvo. Si compartes el acceso con alguien de tu equipo, lo que esa persona haga cuenta como tuyo.',
        en: 'You need an account and a verified email. You are responsible for what happens under your account and for keeping your password safe. If you share access with someone on your team, what they do counts as yours.',
      },
      {
        es: 'Una cuenta es para un negocio. Si tienes varios, necesitas una cuenta para cada uno.',
        en: 'One account is for one business. If you have several, you need an account for each.',
      },
    ],
  },
  {
    heading: { es: 'Gratis y de paga', en: 'Free and paid' },
    body: [
      {
        es: 'Puedes crear una cuenta sin pagar y registrar tus números a mano, con un panel básico. La suscripción abre los insights con IA, el inventario, la conexión automática con tus servicios de cobro y las comparaciones de largo plazo.',
        en: 'You can open an account without paying and record your figures by hand, with a basic dashboard. The subscription opens AI insights, inventory, automatic connection to your payment services and long-run comparisons.',
      },
      {
        es: 'La suscripción se cobra por mes a través de Stripe y se renueva sola hasta que la canceles. Puedes cancelar cuando quieras desde Ajustes; conservas el acceso hasta que termine el periodo que ya pagaste. No hacemos reembolsos por periodos parciales, salvo que la ley lo exija.',
        en: 'The subscription is billed monthly through Stripe and renews on its own until you cancel. You can cancel any time from Settings; you keep access until the period you already paid for ends. We do not refund partial periods unless the law requires it.',
      },
      {
        es: 'Podemos cambiar el precio. Si lo hacemos, te avisamos al menos 30 días antes y el precio nuevo aplica hasta tu siguiente renovación.',
        en: 'We may change the price. If we do, we tell you at least 30 days beforehand and the new price applies from your next renewal.',
      },
    ],
  },
  {
    heading: { es: 'Tus datos son tuyos', en: 'Your data is yours' },
    body: [
      {
        es: 'Los números de tu negocio te pertenecen. No los vendemos, no los rentamos, y no los usamos para entrenar modelos. Los usamos para operar el servicio que contrataste — nada más.',
        en: 'Your business figures belong to you. We do not sell them, rent them, or use them to train models. We use them to run the service you signed up for — nothing else.',
      },
      {
        es: 'Puedes borrar tu cuenta cuando quieras. Al hacerlo eliminamos tus datos de negocio; lo que la ley nos obligue a conservar (registros de facturación, por ejemplo) se guarda el tiempo mínimo exigido.',
        en: 'You can delete your account whenever you want. When you do we remove your business data; anything the law requires us to keep — billing records, for example — is held for the minimum required time.',
      },
    ],
  },
  {
    heading: { es: 'Lo que la IA sí y no hace', en: 'What the AI does and does not do' },
    body: [
      {
        es: 'Los insights se arman en dos partes. Reglas fijas deciden qué está pasando con tus números — si van a la alza, qué lo está moviendo, qué tan grave es. Un modelo de lenguaje solo lo redacta en español claro. El modelo no decide cuál es tu problema, solo cómo decírtelo.',
        en: 'Insights are built in two parts. Fixed rules decide what is happening to your figures — whether they are rising, what is driving it, how serious it is. A language model only phrases that in plain words. The model does not decide what your problem is, only how to say it.',
      },
      {
        es: 'Aun así, son estimaciones y sugerencias, no consejo financiero ni fiscal. Las decisiones de tu negocio son tuyas y de quien te asesore.',
        en: 'Even so, they are estimates and suggestions, not financial or tax advice. Your business decisions are yours and your advisers’.',
      },
      {
        es: 'El uso de IA tiene un límite semanal por cuenta para que el servicio siga siendo sostenible.',
        en: 'AI use has a weekly cap per account so the service stays sustainable.',
      },
    ],
  },
  {
    heading: { es: 'Conectar tus servicios de cobro', en: 'Connecting your payment services' },
    body: [
      {
        es: 'Puedes conectar Mercado Pago, Clip o Shopify para que tus ventas entren solas. Al conectarlos nos autorizas a leer tus ventas — únicamente eso. No podemos cobrar, reembolsar, ni mover dinero desde esas conexiones.',
        en: 'You can connect Mercado Pago, Clip or Shopify so your sales come in on their own. Connecting them authorises us to read your sales — that alone. We cannot charge, refund, or move money through those connections.',
      },
      {
        es: 'Puedes desconectarlos cuando quieras desde Ajustes. Al desconectar, borramos las credenciales guardadas.',
        en: 'You can disconnect them any time from Settings. On disconnecting, we delete the stored credentials.',
      },
    ],
  },
  {
    heading: { es: 'Programa de afiliados', en: 'Affiliate programme' },
    body: [
      {
        es: 'Si participas en el programa de afiliados, aplican además los términos del programa que aceptas al registrar tus datos de pago. En resumen: ganas 60% de lo que pague cada negocio que refieras mientras siga suscrito, las comisiones se retienen 30 días antes de poder pagarse, y referirte a ti mismo no cuenta.',
        en: 'If you take part in the affiliate programme, its own terms also apply — the ones you accept when you enter your payout details. In short: you earn 60% of what each business you refer pays for as long as it stays subscribed, commissions are held 30 days before they can be paid, and referring yourself does not count.',
      },
    ],
  },
  {
    heading: { es: 'Lo que no puedes hacer', en: 'What you may not do' },
    body: [
      {
        es: 'No uses Quantifyr para nada ilegal, no intentes acceder a datos de otras cuentas, no revendas el servicio como si fuera tuyo, y no lo satures con automatizaciones que degraden el servicio para los demás.',
        en: 'Do not use Quantifyr for anything unlawful, do not try to reach other accounts’ data, do not resell the service as your own, and do not flood it with automation that degrades it for everyone else.',
      },
      {
        es: 'Podemos suspender una cuenta que haga cualquiera de esas cosas. Si lo hacemos, te decimos por qué.',
        en: 'We may suspend an account that does any of those. If we do, we tell you why.',
      },
    ],
  },
  {
    heading: { es: 'Disponibilidad y responsabilidad', en: 'Availability and liability' },
    body: [
      {
        es: 'Hacemos lo razonable para que el servicio esté disponible, pero no prometemos que nunca falle. Puede haber interrupciones por mantenimiento o por fallas de los servicios de los que dependemos.',
        en: 'We do what is reasonable to keep the service available, but we do not promise it will never fail. There can be interruptions for maintenance or because a service we depend on fails.',
      },
      {
        es: 'Hasta donde la ley lo permita, nuestra responsabilidad se limita a lo que hayas pagado por el servicio en los últimos 12 meses. No respondemos por decisiones de negocio tomadas a partir de las lecturas de la app.',
        en: 'To the extent the law allows, our liability is limited to what you have paid for the service over the last 12 months. We are not liable for business decisions taken from the app’s readings.',
      },
      {
        es: 'Guarda tus propios respaldos de lo que sea crítico para tu negocio.',
        en: 'Keep your own backups of anything critical to your business.',
      },
    ],
  },
  {
    heading: { es: 'Cambios y ley aplicable', en: 'Changes and governing law' },
    body: [
      {
        es: 'Podemos actualizar estos términos. Si el cambio es importante, te avisamos dentro de la app antes de que aplique. Estos términos se rigen por las leyes de los Estados Unidos Mexicanos.',
        en: 'We may update these terms. If a change is significant we tell you inside the app before it takes effect. These terms are governed by the laws of Mexico.',
      },
    ],
  },
];

// ── Privacy ─────────────────────────────────────────────────────────────────

export const PRIVACY: Section[] = [
  {
    heading: { es: 'Quién es responsable de tus datos', en: 'Who is responsible for your data' },
    body: [
      {
        es: `${OPERATOR.legalName}, con domicilio en ${OPERATOR.address}, es responsable del tratamiento de tus datos personales. Para cualquier asunto de este aviso, escribe a ${OPERATOR.email}.`,
        en: `${OPERATOR.legalName}, at ${OPERATOR.address}, is responsible for handling your personal data. For anything in this notice, write to ${OPERATOR.email}.`,
      },
    ],
  },
  {
    heading: { es: 'Qué datos recabamos', en: 'What data we collect' },
    body: [
      {
        es: 'De tu cuenta: tu correo electrónico y tu contraseña cifrada. Si entras con Google, tu correo y nombre según Google nos los entregue.',
        en: 'From your account: your email and your hashed password. If you sign in with Google, your email and name as Google provides them.',
      },
      {
        es: 'De tu negocio: nombre, giro, moneda, la descripción que escribas, tus metas, tus costos fijos, y tus registros de ventas, clientes, costos e inventario.',
        en: 'From your business: name, trade, currency, the description you write, your goals, your fixed costs, and your records of sales, customers, costs and stock.',
      },
      {
        es: 'Si conectas un servicio de cobro: un token de acceso de solo lectura, cifrado, y los montos y fechas de las ventas que ese servicio reporta.',
        en: 'If you connect a payment service: a read-only access token, encrypted, and the amounts and dates of the sales that service reports.',
      },
      {
        es: 'Si participas como afiliado: tu nombre legal, tu identificación fiscal (RFC, EIN o SSN) y tus datos bancarios. Estos dos últimos se guardan cifrados con AES-256-GCM y la app nunca vuelve a mostrarlos en pantalla — solo los últimos cuatro dígitos.',
        en: 'If you take part as an affiliate: your legal name, your tax identifier (RFC, EIN or SSN) and your bank details. The last two are stored encrypted with AES-256-GCM and the app never shows them on screen again — only the last four digits.',
      },
      {
        es: 'No recabamos datos de tus clientes finales. Registramos cuántos fueron, no quiénes.',
        en: 'We do not collect data about your own customers. We record how many there were, not who they were.',
      },
    ],
  },
  {
    heading: { es: 'Para qué los usamos', en: 'What we use it for' },
    body: [
      {
        es: 'Para operar el servicio: mostrarte tus números, calcular tus tendencias, generar tus insights, llevar tu inventario y cobrarte la suscripción. Estas finalidades son necesarias; sin ellas no hay servicio.',
        en: 'To run the service: show you your figures, work out your trends, generate your insights, keep your stock and bill your subscription. These purposes are necessary; without them there is no service.',
      },
      {
        es: 'Para avisarte por correo cuando lleves tiempo sin registrar datos. Puedes desactivar esos recordatorios en Ajustes sin perder el servicio.',
        en: 'To email you when you have gone a while without recording anything. You can turn those reminders off in Settings without losing the service.',
      },
      {
        es: 'No usamos tus datos para publicidad, no los vendemos, y no los usamos para entrenar modelos de inteligencia artificial.',
        en: 'We do not use your data for advertising, we do not sell it, and we do not use it to train artificial-intelligence models.',
      },
    ],
  },
  {
    heading: { es: 'Con quién los compartimos', en: 'Who we share it with' },
    body: [
      {
        es: 'Con proveedores que necesitamos para operar, cada uno con acceso solo a lo que su función requiere: Supabase (base de datos y autenticación), Netlify (alojamiento), Stripe (cobros), Sentry (reportes de error), Resend (correos) y el proveedor de modelos de lenguaje que redacta tus insights.',
        en: 'With providers we need in order to operate, each with access only to what its job requires: Supabase (database and authentication), Netlify (hosting), Stripe (payments), Sentry (error reports), Resend (email) and the language-model provider that phrases your insights.',
      },
      {
        es: 'Sobre la IA, con claridad: cuando generas un insight, enviamos un resumen de tus cifras — cambios porcentuales, tendencias, el giro de tu negocio — a un proveedor de modelos de lenguaje para que lo redacte. No enviamos tu correo, tu nombre ni tus datos bancarios.',
        en: 'On the AI, plainly: when you generate an insight we send a summary of your figures — percentage changes, trends, your trade — to a language-model provider so it can phrase them. We do not send your email, your name or your bank details.',
      },
      {
        es: 'Algunos de estos proveedores están fuera de México. Al usar el servicio consientes esa transferencia internacional, que es necesaria para prestarlo.',
        en: 'Some of these providers are outside Mexico. By using the service you consent to that international transfer, which is necessary to provide it.',
      },
      {
        es: 'También compartiríamos datos si una autoridad competente nos lo exigiera por ley.',
        en: 'We would also share data if a competent authority required it by law.',
      },
    ],
  },
  {
    heading: { es: 'Cómo los protegemos', en: 'How we protect it' },
    body: [
      {
        es: 'Cada cuenta solo puede leer sus propios datos, y eso se aplica en la base de datos misma, no solo en la app. Las credenciales de tus servicios de cobro y tus datos fiscales y bancarios se guardan cifrados. Los reportes de error se limpian de valores sensibles antes de salir.',
        en: 'Each account can read only its own data, and that is enforced in the database itself, not just in the app. Your payment-service credentials and your tax and bank details are stored encrypted. Error reports are scrubbed of sensitive values before they leave.',
      },
      {
        es: 'Ningún sistema es invulnerable. Si ocurriera una violación que afecte tus datos, te lo comunicaríamos.',
        en: 'No system is invulnerable. If a breach affecting your data occurred, we would tell you.',
      },
    ],
  },
  {
    heading: { es: 'Tus derechos ARCO', en: 'Your ARCO rights' },
    body: [
      {
        es: 'Tienes derecho a acceder a tus datos, rectificarlos si son inexactos, cancelarlos cuando consideres que no se requieren, y oponerte a su uso para fines específicos. También puedes revocar tu consentimiento.',
        en: 'You have the right to access your data, correct it if it is inaccurate, cancel it when you consider it unnecessary, and object to its use for specific purposes. You may also withdraw your consent.',
      },
      {
        es: `Para ejercerlos, escribe a ${OPERATOR.email} desde el correo de tu cuenta, diciendo qué derecho quieres ejercer y sobre qué datos. Respondemos en un máximo de 20 días hábiles.`,
        en: `To exercise them, write to ${OPERATOR.email} from your account email, saying which right you want to exercise and over what data. We reply within 20 business days at most.`,
      },
      {
        es: 'Muchos de estos derechos los puedes ejercer tú directamente: editar o borrar tus registros desde la app, desconectar un servicio de cobro desde Ajustes, o eliminar tu cuenta completa.',
        en: 'Many of these you can exercise yourself: edit or delete your records from the app, disconnect a payment service from Settings, or delete your whole account.',
      },
    ],
  },
  {
    heading: { es: 'Cuánto tiempo los guardamos', en: 'How long we keep it' },
    body: [
      {
        es: 'Mientras tu cuenta exista. Al eliminarla, borramos tus datos de negocio. Los registros de facturación se conservan el tiempo que la legislación fiscal exija.',
        en: 'For as long as your account exists. When you delete it, we remove your business data. Billing records are kept for as long as tax law requires.',
      },
    ],
  },
  {
    heading: { es: 'Cookies y sesión', en: 'Cookies and session' },
    body: [
      {
        es: 'Usamos almacenamiento local y cookies para mantener tu sesión abierta y recordar tus preferencias de idioma y tema. También guardamos un identificador aleatorio que dura lo que dura la pestaña, únicamente para no contar cinco veces a quien recarga la página. No identifica a nadie, no te sigue a otros sitios y desaparece al cerrar. No usamos cookies de publicidad ni de seguimiento entre sitios, y no hay servicios de analítica de terceros.',
        en: 'We use local storage and cookies to keep your session open and remember your language and theme preferences. We also keep a random identifier that lasts as long as the browser tab, purely so that one person reloading a page is not counted five times. It identifies nobody, does not follow you to other sites, and disappears when the tab closes. We use no advertising or cross-site tracking cookies, and no third-party analytics.',
      },
    ],
  },
  {
    heading: { es: 'Cambios a este aviso', en: 'Changes to this notice' },
    body: [
      {
        es: 'Si cambiamos este aviso, publicamos la versión nueva aquí y te avisamos dentro de la app cuando el cambio sea relevante.',
        en: 'If we change this notice we publish the new version here and tell you inside the app when the change matters.',
      },
    ],
  },
];

// ── FAQ ─────────────────────────────────────────────────────────────────────

export interface Faq {
  q: { es: string; en: string };
  a: { es: string; en: string };
}

export const FAQ: Faq[] = [
  {
    q: { es: '¿Qué hace Quantifyr exactamente?', en: 'What does Quantifyr actually do?' },
    a: {
      es: 'Registras tus ventas, clientes y costos — a mano o conectando tu servicio de cobro — y la app te dice si vas mejor o peor que antes, qué lo está causando, y qué puedes hacer al respecto. Está pensada para el dueño de un negocio pequeño, no para un contador.',
      en: 'You record your sales, customers and costs — by hand or by connecting your payment service — and the app tells you whether you are doing better or worse than before, what is causing it, and what you can do about it. It is built for a small business owner, not an accountant.',
    },
  },
  {
    q: { es: '¿Necesito saber de contabilidad?', en: 'Do I need to know accounting?' },
    a: {
      es: 'No. Solo necesitas saber cuánto vendiste, cuánta gente vino y cuánto gastaste. La app hace el resto y te lo explica en español normal.',
      en: 'No. You only need to know how much you sold, how many people came and how much you spent. The app does the rest and explains it in plain words.',
    },
  },
  {
    q: { es: '¿Qué puedo hacer sin pagar?', en: 'What can I do without paying?' },
    a: {
      es: 'Crear tu cuenta, registrar tus números a mano y ver tu panel básico con tus ventas y tendencias. La suscripción abre los insights con IA, el inventario, la conexión automática con tus servicios de cobro y las comparaciones de largo plazo.',
      en: 'Open your account, record your figures by hand and see your basic dashboard with your sales and trends. The subscription opens AI insights, inventory, automatic connection to your payment services and long-run comparisons.',
    },
  },
  {
    q: { es: '¿Cómo entra la información de mis ventas?', en: 'How does my sales data get in?' },
    a: {
      es: 'De dos maneras. La escribes tú una vez por semana o por día, o conectas Mercado Pago, Clip o Shopify y entra sola cada día. Si conectas uno, solo le damos permiso de leer tus ventas: la app no puede cobrar ni mover dinero desde ahí.',
      en: 'Two ways. You type it once a week or once a day, or you connect Mercado Pago, Clip or Shopify and it arrives on its own each day. If you connect one, we only take permission to read your sales: the app cannot charge or move money through it.',
    },
  },
  {
    q: { es: '¿Mis números están seguros?', en: 'Is my data safe?' },
    a: {
      es: 'Cada cuenta solo puede leer sus propios datos, y esa regla vive en la base de datos, no solo en la app. Las credenciales de tus servicios de cobro y —si eres afiliado— tu RFC y tus datos bancarios se guardan cifrados, y la app nunca vuelve a mostrarlos completos.',
      en: 'Each account can read only its own data, and that rule lives in the database, not just in the app. Your payment-service credentials and — if you are an affiliate — your tax ID and bank details are stored encrypted, and the app never shows them in full again.',
    },
  },
  {
    q: { es: '¿La IA ve mis datos?', en: 'Does the AI see my data?' },
    a: {
      es: 'Cuando pides un insight, enviamos un resumen de tus cifras a un modelo de lenguaje para que lo redacte: cambios porcentuales, tendencias y el giro de tu negocio. No enviamos tu correo, tu nombre ni tus datos bancarios, y tus datos no se usan para entrenar modelos.',
      en: 'When you ask for an insight we send a summary of your figures to a language model so it can phrase them: percentage changes, trends and your trade. We do not send your email, your name or your bank details, and your data is not used to train models.',
    },
  },
  {
    q: { es: '¿Qué tan confiables son los insights?', en: 'How reliable are the insights?' },
    a: {
      es: 'Lo que dicen sale de reglas fijas sobre tus propios números, no de una opinión del modelo — el modelo solo lo redacta. Aun así son estimaciones, no consejo financiero, y dependen de que lo que registraste sea correcto.',
      en: 'What they say comes from fixed rules over your own figures, not from the model’s opinion — the model only phrases it. They are still estimates, not financial advice, and they depend on what you recorded being right.',
    },
  },
  {
    q: { es: '¿Puedo cancelar cuando quiera?', en: 'Can I cancel any time?' },
    a: {
      es: 'Sí, desde Ajustes, sin llamadas ni trámites. Conservas el acceso hasta que termine el mes que ya pagaste.',
      en: 'Yes, from Settings, with no calls and no paperwork. You keep access until the month you already paid for ends.',
    },
  },
  {
    q: { es: '¿Puedo borrar mi cuenta y mis datos?', en: 'Can I delete my account and my data?' },
    a: {
      es: 'Sí. Al borrarla eliminamos tus datos de negocio. Solo conservamos los registros de facturación que la ley fiscal nos obliga a guardar.',
      en: 'Yes. When you delete it we remove your business data. We keep only the billing records tax law requires us to hold.',
    },
  },
  {
    q: {
      es: '¿Cómo funciona el programa de afiliados?',
      en: 'How does the affiliate programme work?',
    },
    a: {
      es: 'Eliges tu propio código, lo compartes, y ganas 60% de lo que pague cada negocio que refieras mientras siga suscrito. A quien refieras le damos un descuento por usarlo. Las comisiones se retienen 30 días antes de poder pagarse, por si hay un reembolso o contracargo. Referirte a ti mismo no cuenta.',
      en: 'You pick your own code, share it, and earn 60% of what each business you refer pays for as long as it stays subscribed. Whoever you refer gets a discount for using it. Commissions are held 30 days before they can be paid, in case of a refund or chargeback. Referring yourself does not count.',
    },
  },
  {
    q: {
      es: '¿Sirve si soy el único que trabaja en mi negocio?',
      en: 'Is it useful if I run my business alone?',
    },
    a: {
      es: 'Sí — de hecho está pensada para eso. Registrar cuesta un minuto a la semana, y a cambio dejas de adivinar si el mes fue bueno.',
      en: 'Yes — it is built for exactly that. Recording takes a minute a week, and in exchange you stop guessing whether the month was good.',
    },
  },
  {
    q: { es: '¿Y si necesito ayuda?', en: 'What if I need help?' },
    a: {
      es: `Escríbenos a ${OPERATOR.email}. También puedes preguntarle a Quant, el asistente dentro de la app, sobre tus propios números o sobre cómo funciona algo.`,
      en: `Write to us at ${OPERATOR.email}. You can also ask Quant, the assistant inside the app, about your own figures or about how something works.`,
    },
  },
];
